import { useState, useEffect } from 'react';
import { TextInput, Select } from '@etherisc/ui-kit';
import type { Product } from '../../../types/product';
import { productService } from '../../../services/productService';
import { ApiError } from '../../../utils/errorHandling';
import { useAuth } from '../../../hooks/useAuth';
import { getActivityById } from '../../../config/products';
import {
  PackageIcon,
  InfoIcon,
  CheckCircleIcon,
} from 'lucide-react';
import type { WizardStepProps } from '../../../framework/wizard';
import type { QuoteWizardData } from '../QuoteWizard';
import { createCompleteQuote } from '../utils/quoteHelpers';
import type { CoverageItemType } from '../../../types/quote';

import { coverageItems } from '../../../config/products/product';

const getCoverageItemOptions = () => coverageItems.map(item => ({
  value: item.id,
  label: item.name,
  icon: item.icon,
  description: item.description
}));

export function ProductSelectionStepNew({ 
  data, 
  updateData, 
  errors 
}: WizardStepProps<QuoteWizardData>) {
  
  // Note: Using shared createCompleteQuote utility to avoid code duplication
  
  // Helper functions to extract coverage config from quote object (single source of truth)
  const getUnderlyingActivities = () => {
    return data.quote?.underlying_assets?.activities_products?.map((a: { type: string }) => a.type) || [];
  };
  
  const getCoverageItems = () => {
    return data.quote?.coverageItems?.filter(c => c.selected).map(c => c.type) || [];
  };
  
  // Note: Product data tracking working correctly
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productError, setProductError] = useState<string | null>(null);
  const [availableActivities, setAvailableActivities] = useState<string[]>([]);

  // Get specific errors for this step - be more specific to avoid false matches
  const productError_validation = errors.find(e => e.includes('select a product') || e.includes('product selected'));
  const templateError = errors.find(e => e.includes('template') || e.includes('name'));
  
  // Debug: Track validation errors
  // Note: Validation errors tracking working correctly
  // const activitiesError = errors.find(e => e.includes('activity') || e.includes('activities'));
  // const coverageError = errors.find(e => e.includes('coverage item'));

  // Load products on component mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        setProductError(null);
        
        // Don't clear products array during reload to prevent dropdown clearing
        // setProducts([]);
        
        // Fetch active products from user's organization only
        const filters = { 
          status: 'ACTIVE' as const,
          // Add organization filter if user has one
          ...(user?.organization?.id && { organizationId: user.organization.id })
        };
        
        // Note: Product loading working correctly
        const response = await productService.getProducts(1, filters, 50, false); // viewAllOrgs: false
        // Note: Product loading successful
        setProducts(response.products);
      } catch (error) {
        console.error('Error loading products:', error);
        if (error instanceof ApiError) {
          setProductError(error.message);
        } else {
          setProductError('Failed to load products. Please try again.');
        }
      } finally {
        setLoadingProducts(false);
      }
    };

    // Only load products if user is available
    if (user) {
      loadProducts();
    }
  }, [user]);

  // Extract activities from selected product and validate against hardcoded config
  useEffect(() => {
    if (data.quote?.product?.productConfig) {
      try {
        const config = data.quote.product.productConfig as unknown as Record<string, unknown>;
        
        // Validate correct schema-wrapped format
        if (!config?.data) {
          throw new Error(`Product config missing required 'data' wrapper. Expected schema-wrapped format with data, schemaId, and schemaVersion.`);
        }
        
        if (!config.schemaId || !config.schemaVersion) {
          throw new Error(`Product config missing schema metadata. Expected schemaId and schemaVersion fields.`);
        }
        
        const actualConfig = config.data as Record<string, unknown>;
        const coverageConfig = actualConfig?.coverageConfig as Record<string, unknown>;
        
        if (!coverageConfig) {
          throw new Error(`Product config missing coverageConfig section.`);
        }
        
        const productActivityIds = (coverageConfig?.activitiesOrProducts as string[]) || [];
        
        // Filter to only include activities that exist in both product config and hardcoded config
        const validActivityIds = productActivityIds.filter(activityId => {
          const activity = getActivityById(activityId);
          return activity !== undefined;
        });
        
        setAvailableActivities(validActivityIds);
        
        // Note: Removed activity validation - now handled via quote object
      } catch (error) {
        console.error('Error parsing product config:', error);
        const errorMessage = error instanceof Error ? error.message : 'Invalid product configuration format';
        console.error('ProductSelectionStep - Configuration Error:', errorMessage);
        
        // Set error state instead of silently failing
        setAvailableActivities([]);
      }
    } else {
      setAvailableActivities([]);
    }
  }, [data.quote?.product]); // Only re-run when product changes, not when activities change

  // Product selection options for dropdown
  const productOptions = products.map(product => ({
    value: product.id,
    label: `${product.name} (${product.code})`
  }));
  
  // Debug: Track productOptions changes
  useEffect(() => {
    // Note: Product options working correctly
  }, [products, productOptions, data.quote?.product?.id]);

  const handleProductChange = (productId: string) => {
    const product = products.find(p => p.id === productId);
    
    const isProductChanged = data.quote?.productId && data.quote.productId !== productId;
    
    // Update quote object with product info, clearing calculation data if product changed
    const updatedQuote = createCompleteQuote(data, {
      product,
      productId,
      ...(isProductChanged ? {
        quotationResults: undefined,
        quotationJobId: undefined,
        protectionLevels: undefined,
        selectedScenarios: undefined,
        premiumAmount: 0,
        perils_config: { perils: [], total_weight_check: 0 },
        locationValidation: undefined,
      } : {})
    });
    
    updateData({ 
      quote: updatedQuote,
      ...(isProductChanged ? {
        recalcNeeded: true,
        pricingCalculation: undefined,
      } : {})
    });
  };

  const handleTemplateNameChange = (templateName: string) => {
    // Update template name in quote object (single source of truth)
    const updatedQuote = createCompleteQuote(data, {
      templateName: templateName.trim() || ''
    });
    updateData({ quote: updatedQuote });
  };

  if (loadingProducts) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading products...</span>
      </div>
    );
  }

  if (productError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <InfoIcon className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Products</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{productError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if quote is approved (read-only mode)
  const isReadOnly = data.quote?.state === 'approved';

  return (
    <div className={`space-y-6 ${isReadOnly ? 'pointer-events-none opacity-60' : ''}`}>
      {/* Product Selection */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PackageIcon className="h-5 w-5 text-blue-600" />
            <h4 className="text-lg font-medium text-gray-900">Choose Insurance Product</h4>
          </div>
          {isReadOnly && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircleIcon className="h-5 w-5" />
              <span className="text-sm font-medium">Approved</span>
            </div>
          )}
        </div>
        
        <Select
          key="product-select" // Stable key to prevent re-mounting
          label="Product *"
          placeholder="Select an insurance product..."
          options={productOptions}
          value={(() => {
            const value = data.quote?.product?.id || '';
            // Note: Product selection working correctly
            return value;
          })()}
          onValueChange={handleProductChange}
          error={productError_validation}
        />

        {/* Product Details */}
        {data.quote?.product && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-medium text-blue-900 mb-2">{data.quote?.product?.name}</h5>
            <p className="text-sm text-blue-800 mb-3">{data.quote?.product?.description}</p>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-900">Version:</span>
                <span className="ml-2 text-blue-800">{data.quote?.product?.version}</span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Code:</span>
                <span className="ml-2 text-blue-800">{data.quote?.product?.code}</span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Coverage Period:</span>
                <span className="ml-2 text-blue-800">
                  {(() => {
                    const config = data.quote?.product?.productConfig as unknown as Record<string, unknown>;
                    const actualConfig = config?.data || config;
                    const coverageConfig = (actualConfig as Record<string, unknown>)?.coverageConfig as Record<string, unknown>;
                    const limits = coverageConfig?.coveragePeriodLimits as Record<string, unknown>;
                    
                    if (limits?.type === 'duration_days') {
                      const minDays = limits?.minDurationDays || 'N/A';
                      const maxDays = limits?.maxDurationDays || 'N/A';
                      return `${minDays}-${maxDays} days`;
                    } else if (limits?.type === 'date_range') {
                      const startRange = limits?.startDateRange as Record<string, unknown>;
                      const endRange = limits?.endDateRange as Record<string, unknown>;
                      
                      const startEarliest = startRange?.earliest ? new Date(startRange.earliest as string).toLocaleDateString() : 'N/A';
                      const startLatest = startRange?.latest ? new Date(startRange.latest as string).toLocaleDateString() : 'N/A';
                      const endEarliest = endRange?.earliest ? new Date(endRange.earliest as string).toLocaleDateString() : 'N/A';
                      const endLatest = endRange?.latest ? new Date(endRange.latest as string).toLocaleDateString() : 'N/A';
                      
                      return (
                        <div>
                          <div>Start: {startEarliest} - {startLatest}</div>
                          <div>End: {endEarliest} - {endLatest}</div>
                        </div>
                      );
                    } else {
                      return 'Not configured';
                    }
                  })()}
                </span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Tags:</span>
                <span className="ml-2 text-blue-800">{data.quote?.product?.tags?.join(', ')}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Template Name (Optional) */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">Quote Template (Optional)</h4>
        <TextInput
          label="Template Name"
          placeholder="Enter a name if you want to save this as a template..."
          value={data.quote?.templateName || ''}
          onChange={(e) => handleTemplateNameChange(e.target.value)}
          error={templateError}
        />
        <p className="text-sm text-gray-600 mt-1">
          If provided, this quote configuration can be reused as a template for future quotes
        </p>
      </div>

      {/* Underlying Activities/Products Selection */}
      {data.quote?.product && (
        <div className="border border-gray-200 rounded-lg">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
            <h4 className="text-lg font-medium text-gray-900">Underlying Activities/Products</h4>
            <p className="text-sm text-gray-600">
              Select the activities or products that will be covered by this insurance policy.
            </p>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-2 gap-3">
              {availableActivities.length > 0 ? availableActivities.map((activityId) => {
                const activity = getActivityById(activityId);
                if (!activity) return null;
                
                return (
                  <label key={activityId} className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer rounded">
                    <input
                      type="checkbox"
                      checked={getUnderlyingActivities().includes(activityId)}
                      onChange={(e) => {
                        const currentActivities = getUnderlyingActivities();
                        const newActivities = e.target.checked
                          ? [...currentActivities, activityId]
                          : currentActivities.filter(a => a !== activityId);
                        
                        // Update quote object with new activities
                        const updatedQuote = createCompleteQuote(data, {
                          underlying_assets: {
                            ...data.quote?.underlying_assets,
                            activities_products: newActivities.map(type => ({ type }))
                          }
                        });
                        
                        updateData({ quote: updatedQuote });
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">{activity.name}</span>
                      <span className="text-xs text-gray-600">{activity.description}</span>
                    </div>
                  </label>
                );
              }) : (
                <div className="col-span-2 text-center text-gray-500 py-4">
                  {data.quote?.product ? 'No activities configured for this product' : 'Please select a product first'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Items Insured Selection */}
      {data.quote?.product && (
        <div className="border border-gray-200 rounded-lg">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
            <h4 className="text-lg font-medium text-gray-900">Items Insured</h4>
            <p className="text-sm text-gray-600">
              Select what specific items or aspects will be covered by the insurance.
            </p>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-2 gap-3">
              {getCoverageItemOptions().map((item) => (
                <label key={item.value} className="flex items-start space-x-3 p-2 hover:bg-gray-50 cursor-pointer rounded">
                  <input
                    type="checkbox"
                    checked={getCoverageItems().includes(item.value as CoverageItemType)}
                    onChange={(e) => {
                      const currentItems = getCoverageItems();
                      const newItems = e.target.checked
                        ? [...currentItems, item.value]
                        : currentItems.filter(i => i !== item.value);
                      
                      // Update quote object with new coverage items
                      const updatedQuote = createCompleteQuote(data, {
                        coverageItems: newItems.map(type => ({ type: type as unknown as CoverageItemType, selected: true }))
                      });
                      
                      updateData({ quote: updatedQuote });
                    }}
                    className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">{item.label}</span>
                      {item.icon && <span className="text-lg">{item.icon}</span>}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Note: Removed duplicate error display - now handled by SimpleWizardShell */}
    </div>
  );
}
